/**
 * TRAC-896: "grow the picker out of whatever you tapped".
 *
 * Every USER-GESTURE entry into the full-screen Find Your Vehicle interface
 * morphs out of the element that was tapped — the inline partial YMMV card, a
 * starter-prompt chip, the dismissed-state "Find my vehicle" button, the vehicle
 * header bar's tire-size and "Choose new vehicle" entries. The origin is always
 * CAPTURED AT CLICK TIME by the button itself, never queried afterwards: several
 * cards/chips can be on screen at once, and the one you tapped is often gone (or
 * inside a `display:none` subtree) by the time the picker closes.
 *
 * An open with no originating element — the agent raising the picker on its own,
 * which only happens with PARTIAL_YMMV_CARD_ENABLED off — deliberately gets no
 * morph. There is nothing to grow out of and inventing an origin would be a lie.
 *
 * WHY AN IMPERATIVE OVERLAY INSTEAD OF A REACT/FLIP ANIMATION
 * -----------------------------------------------------------
 * A true in-place FLIP fights the existing mount structure. When `ymmvMode`
 * flips true, ONE render simultaneously (a) sets `display:none` on the messages
 * wrapper the card lives in, (b) paints the chat container teal, and (c) mounts
 * <VehicleTypeahead/> as a new flex child. The origin's own DOM node is inside
 * the hidden subtree from that render onward, so it has no box to animate from
 * and nothing survives the swap to interpolate.
 *
 * So instead: a single throwaway <div> outside React's tree covers the whole
 * transition. It is created SYNCHRONOUSLY inside the tap handler — before React
 * has flushed anything — and it morphs from the card's rect to the container's
 * rect using nothing but `transform: translate() scale()` (never width/height/
 * top/left: those relayout, and a layout-animated overlay would jank exactly
 * when the real interface is mounting behind it).
 *
 * THE ORDER MATTERS, AND IT IS THE WHOLE TRICK
 * --------------------------------------------
 * The obvious version — flip to ymmvMode immediately and grow the overlay over
 * it — animates NOTHING you can see: the container is teal from frame one, so a
 * teal panel grows across a teal background. Measured, then thrown away.
 *
 * So the swap is HELD until the overlay has finished growing:
 *
 *   0ms            the overlay appears exactly over the origin. The conversation
 *                  is still fully on screen behind it — `ymmvMorphing` holds
 *                  every PRESENTATIONAL ymmvMode gate in ChatInterface off.
 *   0 → 280ms      the panel grows out of the origin and swallows the container.
 *   280ms          `onCover` fires. React does the whole swap in one render —
 *                  teal container, messages hidden, picker mounted — under an
 *                  opaque full-bleed panel, so none of it is visible.
 *   280 → 380ms    the overlay dissolves and the picker's contents rise into
 *                  place. Both layers are the same #133e4d, so the handoff has
 *                  nothing to give away; all you see is content arriving.
 *
 * `onCover` is guaranteed to fire exactly once — on completion OR on cancel —
 * so the visual hold can never get stuck on.
 *
 * The origin <button> is never itself animated — only this detached overlay.
 * That keeps Playwright's actionability/stability wait off the animated element
 * (this repo has a history of animated controls failing stability waits).
 *
 * Everything here is a no-op under `prefers-reduced-motion: reduce`.
 */

// --- Timing -----------------------------------------------------------------
// Durations in ms. These were tuned by eye against a ~4x slow-motion build; if
// you need to review the motion again, multiply them here rather than
// reintroducing a shipped flag to do it.
/** Panel grow, card rect → full-interface rect. Ends fully covering the container. */
const MORPH_MS = 280;
/** Overlay dissolve, AFTER the swap — this is the reveal, not part of the grow. */
const OVERLAY_FADE_MS = 100;
/** Picker contents rise in as the overlay dissolves (both start at the swap). */
const CONTENT_MS = 200;
/** Close is deliberately a touch quicker than the open; the open is the star. */
const CLOSE_MS = 240;
const CLOSE_FADE_MS = 90;
/** Fallback when the originating card can't be located/measured on close. */
const CLOSE_PLAIN_FADE_MS = 140;

/**
 * The panel's curve. Kept as a string for the plain (non-compensated) content
 * animation; the morph itself samples the same curve numerically via `easeOut`
 * below, so keep the two definitions in sync if either changes.
 */
const EASE_OUT = "cubic-bezier(0.2, 0, 0, 1)";

/**
 * Fallback corner radius when an origin's computed radius can't be read. Real
 * origins vary wildly — the partial card is a 14px panel, a starter-prompt chip
 * is a full pill — so the radius is measured per-origin rather than assumed.
 */
const FALLBACK_RADIUS_PX = 14;


/** Focus delay when no morph runs — the pre-existing behavior. */
const FOCUS_DELAY_STATIC_MS = 50;

// --- Single-morph state -----------------------------------------------------
// Only one morph can ever be in flight (one picker, one overlay). Everything
// cancellable is tracked here so `cancelYmmvMorph()` is a complete teardown:
// a rapid double-tap, an unmount, or a close mid-open all leave nothing behind.

type Phase = "open" | "close";

let phase: Phase | null = null;
let overlay: HTMLDivElement | null = null;
let animations: Animation[] = [];
let frames: number[] = [];
let timers: number[] = [];
/** The card element that was tapped — reused as the close target if still live. */
let originEl: HTMLElement | null = null;
let originRect: Box | null = null;
/** The origin's own corner radius, so a pill chip starts as a pill, not a card. */
let originRadiusPx = FALLBACK_RADIUS_PX;
/**
 * Set by `captureYmmvMorphOrigin`, consumed by `beginYmmvOpenMorph`. This is what
 * keeps a stale origin from leaking across opens: a capture arms exactly one
 * open, and an open that finds no arming clears the origin outright — so an
 * agent-raised picker can never inherit the last chip somebody tapped.
 */
let originArmed = false;
/** Did the open that is currently on screen morph? Gates the closing morph. */
let openMorphed = false;
/**
 * The consumer's "you may swap now" callback, held until the panel has fully
 * covered the container. Consumed exactly once — by the grow animation
 * finishing, or by `cancelYmmvMorph()` — so ChatInterface's visual hold can
 * never be left latched on by an interrupted morph.
 */
let pendingCover: (() => void) | null = null;

const runCover = (): void => {
  const fn = pendingCover;
  pendingCover = null;
  fn?.();
};

/**
 * True while an OPEN morph is holding the swap — the picker has not mounted yet
 * and there is deliberately no dismiss affordance on screen. Callers use this to
 * make opening idempotent: a second tap during the morph must be ignored, never
 * treated as a fresh open (see the note on re-entrancy in beginYmmvOpenMorph).
 */
export const isYmmvOpenMorphInFlight = (): boolean => phase === "open" && !!overlay;

interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

const isReducedMotion = (): boolean => {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
};

const usable = (r: Box | null): r is Box => !!r && r.width > 1 && r.height > 1;

const rectOf = (el: Element): Box => {
  const r = el.getBoundingClientRect();
  return { left: r.left, top: r.top, width: r.width, height: r.height };
};

/**
 * The box the full-screen interface will actually occupy — NOT the container's
 * own rect.
 *
 * Chrome can sit inside the container ABOVE the picker, and it comes in two
 * kinds. Most of it (the live-chat banner, the announcement banner) is hidden in
 * ymmvMode and so is not there once the swap lands. The vehicle header bar is the
 * exception: it renders whenever a vehicle is active and SURVIVES the swap, and —
 * unlike everything else on that screen — it stays light instead of going teal.
 * A panel grown to the container's full rect covers it during the grow and hands
 * it back at the reveal, so a light strip flashes in at the top. This was
 * invisible while the bar was gated behind an experiment arm; the gate is retired,
 * so the container's rect can no longer be taken for the interface's.
 *
 * Note this cannot be measured from anything's CURRENT position: the layout above
 * the interface changes in the very swap being hidden (the banners leave, so the
 * bar slides up). What is stable across the swap is the surviving chrome's HEIGHT,
 * so the interface begins at the container's top plus however much marked chrome
 * is rendered. Anything added later that outlives the swap carries the same
 * marker and stays correct for free — no per-element special-casing here.
 *
 * The bottom deliberately runs to the container's, past the interface itself: the
 * input island below it restyles in the same swap, and it is teal on both sides of
 * that change, so covering it hides the restyle at no visual cost.
 */
const interfaceBox = (container: HTMLElement): Box => {
  const c = rectOf(container);
  let offset = 0;
  container.querySelectorAll<HTMLElement>("[data-ymmv-persistent-chrome]").forEach((el) => {
    offset += el.getBoundingClientRect().height;
  });
  const top = Math.min(c.top + offset, c.top + c.height);
  return { left: c.left, top, width: c.width, height: c.top + c.height - top };
};

/**
 * The element's rendered corner radius, clamped the way the browser clamps it
 * (a `border-radius: 999px` pill on a 36px-tall chip renders as 18px). Reading
 * it means the overlay starts as the *actual* shape that was tapped.
 */
const radiusOf = (el: Element, box: Box): number => {
  const raw = parseFloat(getComputedStyle(el).borderTopLeftRadius);
  const r = Number.isFinite(raw) ? raw : FALLBACK_RADIUS_PX;
  return Math.max(0, Math.min(r, box.width / 2, box.height / 2));
};

/**
 * Evaluate a CSS cubic-bezier easing: given normalized time, return normalized
 * progress. Newton-solves x(t)=time, then reads y(t) — the same thing the
 * compositor does, which is exactly the point (see morphKeyframes).
 */
const bezier = (p1x: number, p1y: number, p2x: number, p2y: number) => {
  const cx = 3 * p1x;
  const bx = 3 * (p2x - p1x) - cx;
  const ax = 1 - cx - bx;
  const cy = 3 * p1y;
  const by = 3 * (p2y - p1y) - cy;
  const ay = 1 - cy - by;
  const x = (t: number): number => ((ax * t + bx) * t + cx) * t;
  const dx = (t: number): number => (3 * ax * t + 2 * bx) * t + cx;
  return (time: number): number => {
    let t = time;
    for (let i = 0; i < 8; i++) {
      const err = x(t) - time;
      if (Math.abs(err) < 1e-6) break;
      const d = dx(t);
      if (Math.abs(d) < 1e-6) break;
      t -= err / d;
    }
    t = Math.min(1, Math.max(0, t));
    return ((ay * t + by) * t + cy) * t;
  };
};

const easeOut = bezier(0.2, 0, 0, 1);
const easeInOut = bezier(0.4, 0, 0.2, 1);

const lerp = (a: number, b: number, p: number): number => a + (b - a) * p;

/**
 * Keyframes that carry the overlay — laid out at `box` — from rect `a` to rect
 * `b`, using transform only.
 *
 * WHY THE EASING IS BAKED IN INSTEAD OF HANDED TO WAAPI
 * ----------------------------------------------------
 * The corner radius has to be pre-divided by the current scale to render as a
 * true circle (a 20px corner on a box scaled to 5% would otherwise render at
 * 1px). Compensating at only the two endpoints and letting the browser
 * interpolate between them does NOT work: the rendered radius is the product of
 * two interpolations, so it bulges quadratically in the middle — the panel
 * visibly balloons into a lozenge halfway through. Caught it in slow-mo.
 *
 * So the curve is sampled here instead: at each step the eased progress is
 * computed, the rect and the DESIRED rendered radius are interpolated at that
 * progress, and the radius is divided by that step's exact scale. The keyframes
 * then play back linearly — the shape is right at every sample, and 24ms apart
 * there is nothing left to drift between them.
 */
const morphKeyframes = (
  box: Box,
  a: Box,
  aRadius: number,
  aShadow: number,
  b: Box,
  bRadius: number,
  bShadow: number,
  ease: (t: number) => number,
  steps = 12,
): Keyframe[] => {
  const out: Keyframe[] = [];
  for (let i = 0; i <= steps; i++) {
    const time = i / steps;
    const p = ease(time);
    const w = lerp(a.width, b.width, p);
    const h = lerp(a.height, b.height, p);
    const sx = w / box.width;
    const sy = h / box.height;
    const tx = lerp(a.left, b.left, p) - box.left;
    const ty = lerp(a.top, b.top, p) - box.top;
    const radius = lerp(aRadius, bRadius, p);
    const shadow = lerp(aShadow, bShadow, p);
    out.push({
      offset: time,
      easing: "linear",
      transform: `translate(${tx}px, ${ty}px) scale(${sx}, ${sy})`,
      // Elliptical, pre-divided by THIS step's scale so it renders as a true
      // `radius` circle. Clamped to half the unscaled overlay so a tiny origin
      // can't ask for a radius the browser would have to re-scale.
      borderRadius: `${Math.min(radius / sx, box.width / 2)}px / ${Math.min(radius / sy, box.height / 2)}px`,
      boxShadow: `0 ${10 * shadow}px ${30 * shadow}px rgba(0, 0, 0, ${0.28 * shadow})`,
    });
  }
  return out;
};

const track = (a: Animation | null | undefined): void => {
  if (a) animations.push(a);
};

const nextFrames = (n: number, fn: () => void): void => {
  const step = (remaining: number): void => {
    const id = requestAnimationFrame(() => {
      if (remaining <= 1) fn();
      else step(remaining - 1);
    });
    frames.push(id);
  };
  step(n);
};

/** Tear down the overlay + every animation/frame it owns. Safe to call anytime. */
export const cancelYmmvMorph = (): void => {
  frames.forEach((id) => cancelAnimationFrame(id));
  frames = [];
  timers.forEach((id) => clearTimeout(id));
  timers = [];
  animations.forEach((a) => {
    a.onfinish = null;
    try {
      a.cancel();
    } catch {
      /* already finished */
    }
  });
  animations = [];
  overlay?.remove();
  overlay = null;
  phase = null;
  // Always release the visual hold, even on an aborted morph — otherwise the
  // conversation would stay on screen with ymmvMode already true.
  runCover();
};

/**
 * Cancel only an in-flight OPEN morph. Used when the picker closes for a reason
 * that isn't the X button (e.g. a tire size was committed) — the overlay would
 * otherwise sit on top of the returning conversation until it finished.
 * Deliberately leaves a running CLOSE morph alone.
 */
export const cancelYmmvOpenMorph = (): void => {
  if (phase === "open") cancelYmmvMorph();
  // Whatever just happened, the open this flag described is over. A close morph
  // that wanted it has already consumed it (it runs before deactivation); any
  // other exit — a committed tire size, a reset — must not leave it armed for
  // some later, unrelated close.
  openMorphed = false;
};

/**
 * Record which element was tapped, from that element's own click handler — the
 * only place that knows which of several on-screen cards/chips it was. Read back
 * by `beginYmmvOpenMorph`, and again on close as the shrink target.
 *
 * Pass null for an open with no originating element; that clears any previous
 * origin so it can't be inherited.
 */
export const captureYmmvMorphOrigin = (el: HTMLElement | null): void => {
  if (!el) {
    originEl = null;
    originRect = null;
    originArmed = false;
    return;
  }
  originEl = el;
  originRect = rectOf(el);
  originRadiusPx = radiusOf(el, originRect);
  originArmed = true;
};

const makeOverlay = (doc: Document, to: Box): HTMLDivElement => {
  const el = doc.createElement("div");
  el.setAttribute("data-ymmv-morph", "1");
  el.setAttribute("aria-hidden", "true");
  Object.assign(el.style, {
    position: "fixed",
    left: `${to.left}px`,
    top: `${to.top}px`,
    width: `${to.width}px`,
    height: `${to.height}px`,
    // Same flat panel as the card and as the ymmvMode container, so the
    // cross-fade at the end has nothing to give away.
    background: "#133e4d",
    transformOrigin: "0 0",
    willChange: "transform, opacity",
    // SWALLOW input for as long as it is up. This started life as
    // `pointer-events: none` so the harness would never have to wait on it, and
    // that was a bug with teeth: the panel is opaque and the conversation is
    // still mounted underneath during the open (the swap is held), so taps went
    // straight through to a UI the customer could no longer see. Tapping the
    // origin again re-entered the open and restarted the morph, so an impatient
    // double-tap could keep the picker from ever arriving — and until it
    // arrives there is no X, i.e. no way out. What is under an opaque cover is
    // not there; it must not be clickable.
    pointerEvents: "auto",
    zIndex: "10050",
  } as Partial<CSSStyleDeclaration>);
  return el;
};

/**
 * Start the opening morph. Call SYNCHRONOUSLY in the tap handler, before the
 * state update that activates YMMV mode.
 *
 * @param container the chat container that turns teal in ymmvMode. The target is
 *                  derived from it via `interfaceBox` — NOT its own rect, which
 *                  includes persistent chrome the picker never covers (measured
 *                  now; the interface slot does not resize on the flip).
 * @param onCover   invoked once the panel has fully covered the container: the
 *                  cue to drop the visual hold and let the swap render. ALWAYS
 *                  called exactly once, including if the morph is cancelled.
 * @returns true if a morph is running — the caller must then hold the swap until
 *          `onCover`. false means reduced motion / unmeasurable: swap instantly.
 */
export const beginYmmvOpenMorph = (
  container: HTMLElement | null,
  onCover: () => void,
): boolean => {
  cancelYmmvMorph();
  // Consume the arming. An open that wasn't preceded by a capture has no origin
  // — and must not silently reuse the last one, which would grow the picker out
  // of some unrelated chip and shrink it back into the wrong place on close.
  const armed = originArmed;
  originArmed = false;
  openMorphed = false;
  if (!armed) {
    originEl = null;
    originRect = null;
  }
  if (isReducedMotion() || !container || !usable(originRect)) return false;

  const from: Box = originRect;
  const to = interfaceBox(container);
  if (!usable(to)) return false;

  const doc = container.ownerDocument;
  const endRadius = radiusOf(container, to);
  // A soft lift at the start so the panel reads as rising off the conversation
  // rather than being painted onto it; gone by the time it lands (nothing left
  // to lift off).
  const frames = morphKeyframes(to, from, originRadiusPx, 1, to, endRadius, 0, easeOut);

  const el = makeOverlay(doc, to);
  // Paint the start state immediately: the overlay has to be origin-shaped in
  // the very first frame, sitting on top of the still-visible conversation.
  Object.assign(el.style, {
    transform: frames[0].transform as string,
    borderRadius: frames[0].borderRadius as string,
    boxShadow: frames[0].boxShadow as string,
  });
  doc.body.appendChild(el);

  overlay = el;
  phase = "open";
  openMorphed = true;
  pendingCover = onCover;

  // Easing is baked into the keyframes (see morphKeyframes) — playback is linear.
  const grow = el.animate(frames, { duration: MORPH_MS, easing: "linear", fill: "both" });
  track(grow);

  // Watchdog. While the swap is held there is no picker and therefore no X, so a
  // cover callback that never arrives is not a cosmetic glitch — it is a dead
  // end with no exit. Animation events can be lost (backgrounded tab, a throw in
  // a handler, a WAAPI edge), so the hold gets a hard ceiling: tear down, which
  // releases the cover and lets the picker mount regardless.
  timers.push(
    window.setTimeout(() => {
      if (phase === "open") cancelYmmvMorph();
    }, MORPH_MS + OVERLAY_FADE_MS + 600),
  );

  grow.onfinish = () => {
    if (phase !== "open" || !overlay) return;
    // Fully covered — let React do the entire swap in one render, unseen.
    runCover();
    // Dissolve the cover and float the picker's contents in behind it. Same
    // #133e4d on both layers, so the crossover is invisible: what the eye reads
    // is simply the content arriving in a panel that is already there.
    const reveal = overlay.animate([{ opacity: 1 }, { opacity: 0 }], {
      duration: OVERLAY_FADE_MS,
      easing: "linear",
      fill: "forwards",
    });
    track(reveal);
    reveal.onfinish = () => {
      if (phase === "open") cancelYmmvMorph();
    };
    nextFrames(2, () => {
      const content = container.querySelector<HTMLElement>("[data-ymmv-interface]");
      if (!content) return;
      track(
        content.animate(
          [
            { opacity: 0, transform: "translateY(10px)" },
            { opacity: 1, transform: "translateY(0px)" },
          ],
          { duration: CONTENT_MS, easing: EASE_OUT, fill: "backwards" },
        ),
      );
    });
  };

  return true;
};

/**
 * Start the closing morph, mirroring whatever the open did: cover everything at
 * full bounds NOW (hiding the un-swap), then — once React has put the
 * conversation back and the origin has a box again — shrink toward it.
 *
 * Self-gating, so callers don't have to know how the picker was opened:
 *  - the open didn't morph (agent-raised, reduced motion) → returns false, the
 *    picker closes instantly exactly as it always has;
 *  - the open morphed but the origin is gone or scrolled off (a starter-prompt
 *    chip that the conversation replaced) → plain fade, never a shrink into
 *    empty space or into whatever element happens to sit there now.
 *
 * Must be called BEFORE the state update that leaves ymmvMode.
 */
export const beginYmmvCloseMorph = (container: HTMLElement | null): boolean => {
  const morphed = openMorphed;
  openMorphed = false;
  cancelYmmvMorph();
  if (!morphed || isReducedMotion() || !container) return false;

  const to = interfaceBox(container);
  if (!usable(to)) return false;

  const el = makeOverlay(container.ownerDocument, to);
  const endRadius = radiusOf(container, to);
  el.style.transform = "translate(0px, 0px) scale(1, 1)";
  el.style.borderRadius = `${endRadius}px`;
  container.ownerDocument.body.appendChild(el);
  overlay = el;
  phase = "close";

  // Watchdog, mirroring the open morph's. The teardown below is driven by an
  // animation finish event, and finish events only dispatch while the page is
  // RENDERING — dismiss in an occluded window or a hidden tab and the event
  // starves, leaving this opaque, input-swallowing panel over the conversation
  // until the window happens to render again. (Reproduced: hide the tab, click
  // the X, force two frames — the animations reach "finished" on the timeline
  // but onfinish never fires.) Timers, unlike animation events, do fire in
  // hidden tabs (coarsened to ~1s, which is fine); past the deadline nobody is
  // watching an animation anyway, so tear down hard.
  timers.push(
    window.setTimeout(() => {
      if (phase === "close") cancelYmmvMorph();
    }, CLOSE_MS + 600),
  );

  // Measure the origin AFTER the conversation is back on screen — while ymmvMode
  // is true the whole messages wrapper is display:none, so its rect is all zeros
  // and there is nothing to aim at.
  nextFrames(2, () => {
    if (!overlay) return;
    const live = originEl && originEl.isConnected ? rectOf(originEl) : null;
    const onScreen =
      usable(live) && live.top < to.top + to.height && live.top + live.height > to.top;

    if (usable(live) && onScreen) {
      // Mirror of the open: full bounds → the origin, shadow fading back in as
      // the panel lifts off the conversation on its way down.
      track(
        overlay.animate(
          morphKeyframes(to, to, endRadius, 0, live, originRadiusPx, 1, easeInOut),
          { duration: CLOSE_MS, easing: "linear", fill: "both" },
        ),
      );
      const fade = overlay.animate([{ opacity: 1 }, { opacity: 0 }], {
        duration: CLOSE_FADE_MS,
        delay: CLOSE_MS - CLOSE_FADE_MS,
        easing: "linear",
        fill: "forwards",
      });
      track(fade);
      fade.onfinish = cancelYmmvMorph;
      return;
    }

    // Origin unreachable (chip replaced by the conversation, cleared session,
    // scrolled far off) — just dissolve.
    const fade = overlay.animate([{ opacity: 1 }, { opacity: 0 }], {
      duration: CLOSE_PLAIN_FADE_MS,
      easing: "linear",
      fill: "forwards",
    });
    track(fade);
    fade.onfinish = cancelYmmvMorph;
  });

  return true;
};

/**
 * How long to wait before focusing the vehicle search input. Focusing early
 * raises the mobile keyboard mid-flight, which resizes the viewport out from
 * under a transform computed against the old bounds — so hold until the panel
 * has landed and the swap has happened. Without a morph this is the original
 * 50ms, unchanged.
 */
export const ymmvOpenFocusDelayMs = (): number =>
  phase === "open" && overlay ? MORPH_MS : FOCUS_DELAY_STATIC_MS;
